// TerminContextMenu.tsx — Rechtsklick-Menü am Terminblock (Termine-SP4, A14):
// „Verschieben" (Quick-Reschedule-Dialog, Entscheidung 63) und „Teams-Meeting
// teilnehmen" (nur bei Online-Meetings). RBC hat kein natives Kontextmenü-API —
// der Custom-Event-Renderer (OutlookKalender) fängt onContextMenu ab und
// liefert Event + Mausposition; hier wird das Menü als fixed-Portal gerendert
// (RdPopover-Pattern, Klemmung über clampPopoverPosition).
//
// „Verschieben" wechselt in einen kompakten Formular-Modus (Datum + Startzeit,
// native Inputs wie im TerminFormWindow): die DAUER bleibt konstant, der PATCH
// läuft über die bestehende calendarService.updateEvent-Pipeline (naive-lokale
// ISO + timeZone 'Europe/Berlin' — Doppel-Offset-Falle beachtet). Read-only-
// Kalender: „Verschieben" ist deaktiviert (Backend sichert doppelt über
// assertCalendarWrite ab); Serieninstanzen sind wie beim Drag nicht
// verschiebbar (canEditEvent).
//
// T-269: „Duplizieren…" öffnet ein Neuanlage-Fenster mit dem Termin als
// Vorlage. Bewusst ohne WRITE-Gate: das Duplikat landet im eigenen Kalender,
// also lässt sich auch ein Termin aus einem Lese-Kalender als Vorlage nehmen.
// Nicht bei fremden privaten (maskierten) Terminen — kopiert würde nur die Maske.

import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "../windowManager/browserFenster/fensterPortal";
import moment from "moment";
import {
    Add16Regular,
    CalendarArrowRight16Regular,
    Copy16Regular,
    LinkSquare16Regular,
    Video16Regular,
} from "@fluentui/react-icons";
import { calendarService } from "../../services/calendarService";
import { useCalendarState } from "./CalendarStateContext";
import type { CalendarGridEvent } from "./types";
import { clampPopoverPosition, naiveIso, verschobenerZeitraum, zeitraumLabel } from "./peek";
import type { PeekAnchor } from "./peek";
import {
    NeuerVorgangAusTerminDialog,
    VorgangZuweisenDialog,
    type TerminVorgangKontext,
} from "./TerminVorgangDialog";
import { hinweisen } from "../redesign/bestaetigen";
import { alleFenster, aktivesFenster } from "../windowManager/browserFenster/fensterRegister";

/** Ein offenes Kontextmenü: Event + Mausposition (Viewport-Koordinaten). */
export interface TerminCtxMenu {
    event: CalendarGridEvent;
    pos: PeekAnchor;
}

export interface TerminContextMenuProps {
    menu: TerminCtxMenu | null;
    onClose: () => void;
    /** WRITE + keine Serieninstanz — steuert „Verschieben" (wie Drag/Resize). */
    canEditEvent: (e: CalendarGridEvent) => boolean;
    /** T-269: Termin als Vorlage für einen neuen öffnen. Fehlt ⇒ kein Menüpunkt. */
    onDuplizieren?: (e: CalendarGridEvent) => void;
    /** T-269: darf dieser Termin als Vorlage dienen? (Default: ja) */
    kannDuplizieren?: (e: CalendarGridEvent) => boolean;
}

export const TerminContextMenu: React.FC<TerminContextMenuProps> = ({
    menu,
    onClose,
    canEditEvent,
    onDuplizieren,
    kannDuplizieren,
}) => {
    const { requestReload } = useCalendarState();
    const popRef = useRef<HTMLDivElement>(null);
    const [pos, setPos] = useState<{ top: number; left: number } | null>(null);
    const [modus, setModus] = useState<"menu" | "verschieben">("menu");
    const [datum, setDatum] = useState(""); // YYYY-MM-DD
    const [zeit, setZeit] = useState(""); // HH:mm
    const [saving, setSaving] = useState(false);

    // Bei jedem neuen Menü: zurück in den Menü-Modus, Formular vorbelegen.
    useEffect(() => {
        if (!menu) return;
        setModus("menu");
        setDatum(moment(menu.event.start).format("YYYY-MM-DD"));
        setZeit(moment(menu.event.start).format("HH:mm"));
        setSaving(false);
    }, [menu]);

    // Position messen + klemmen (auch beim Modus-Wechsel — die Größe ändert sich).
    useLayoutEffect(() => {
        if (!menu) {
            setPos(null);
            return;
        }
        const el = popRef.current;
        if (!el) return;
        const r = el.getBoundingClientRect();
        setPos(
            clampPopoverPosition(
                menu.pos,
                { width: r.width, height: r.height },
                { width: aktivesFenster().innerWidth, height: aktivesFenster().innerHeight },
            ),
        );
    }, [menu, modus]);

    // Außenklick/Escape/Scroll/Resize schließen.
    useEffect(() => {
        if (!menu) return;
        const onDown = (e: MouseEvent) => {
            if (popRef.current?.contains(e.target as Node)) return;
            onClose();
        };
        const onKey = (e: KeyboardEvent) => {
            if (e.key === "Escape") onClose();
        };
        const onMove = () => onClose();
        alleFenster.addEventListener("pointerdown", onDown);
        alleFenster.addEventListener("keydown", onKey);
        alleFenster.addEventListener("resize", onMove);
        alleFenster.addEventListener("scroll", onMove, true); // capture: RBC-Scroller
        return () => {
            alleFenster.removeEventListener("pointerdown", onDown);
            alleFenster.removeEventListener("keydown", onKey);
            alleFenster.removeEventListener("resize", onMove);
            alleFenster.removeEventListener("scroll", onMove, true);
        };
    }, [menu, onClose]);

    // Neuer Zeitraum aus Datum + Startzeit (Dauer bleibt) — null bei ungültigem Datum.
    const neu = useMemo(() => {
        if (!menu) return null;
        const d = moment(datum, "YYYY-MM-DD", true);
        if (!d.isValid()) return null;
        return verschobenerZeitraum(
            menu.event.start,
            menu.event.end,
            d.toDate(),
            menu.event.allDay ? null : zeit,
        );
    }, [menu, datum, zeit]);

    const submit = useCallback(async () => {
        if (!menu || !neu) return;
        const ev = menu.event;
        setSaving(true);
        try {
            await calendarService.updateEvent(ev.mailbox, ev.id, {
                start: naiveIso(neu.start),
                end: naiveIso(neu.end),
                timeZone: "Europe/Berlin",
                isAllDay: ev.allDay,
            });
            requestReload();
            onClose();
        } catch (err) {
            console.error("[TerminContextMenu] Verschieben fehlgeschlagen", err);
            void hinweisen({ titel: "Termin nicht verschoben", text: "Der Termin konnte nicht verschoben werden. Bitte versuche es noch einmal.", ton: "danger" });
        } finally {
            setSaving(false);
        }
    }, [menu, neu, requestReload, onClose]);

    /**
     * K2 — der zweite Einstieg ins Verknüpfen, ohne das Platzproblem der
     * Peek-KARTE: das Kontextmenü ist eine Liste. Beide Punkte sind bewusst
     * NICHT write-gegated (E10) — verknüpft werden KMVS-Metadaten am Sidecar,
     * im Graph-Kalender ändert sich nichts.
     *
     * Die Dialog-States liegen ausserhalb des `if (!menu) return null`: das
     * Menü schließt sich beim Öffnen eines Dialogs, der Dialog muss das
     * überleben.
     */
    const [zuweisenKontext, setZuweisenKontext] = useState<{
        mailbox: string;
        eventId: string;
        contactId?: number | null;
    } | null>(null);
    const [neuerVorgangKontext, setNeuerVorgangKontext] = useState<TerminVorgangKontext | null>(
        null,
    );

    const dialoge = (
        <>
            <VorgangZuweisenDialog
                kontext={zuweisenKontext}
                onClose={() => setZuweisenKontext(null)}
                onGebunden={() => {
                    setZuweisenKontext(null);
                    requestReload();
                }}
            />
            <NeuerVorgangAusTerminDialog
                kontext={neuerVorgangKontext}
                onClose={() => setNeuerVorgangKontext(null)}
                onGebunden={() => {
                    setNeuerVorgangKontext(null);
                    requestReload();
                }}
            />
        </>
    );

    if (!menu) return dialoge;
    const ev = menu.event;
    const kannVerschieben = canEditEvent(ev);
    const teamsUrl = ev.onlineMeetingUrl || null;

    return createPortal(
        <div
            ref={popRef}
            className="cal-rd cal-ctx"
            role="menu"
            aria-label="Termin-Aktionen"
            style={{
                position: "fixed",
                top: pos?.top ?? -9999,
                left: pos?.left ?? -9999,
                visibility: pos ? "visible" : "hidden",
            }}
        >
            {modus === "menu" ? (
                <>
                    <button
                        type="button"
                        role="menuitem"
                        className="cal-ctx-item"
                        disabled={!kannVerschieben}
                        title={kannVerschieben ? undefined : "Kein Schreibrecht auf diesen Kalender"}
                        onClick={() => setModus("verschieben")}
                    >
                        <CalendarArrowRight16Regular aria-hidden /> Verschieben
                    </button>
                    {onDuplizieren && (kannDuplizieren?.(ev) ?? true) && (
                        <button
                            type="button"
                            role="menuitem"
                            className="cal-ctx-item"
                            onClick={() => {
                                onDuplizieren(ev);
                                onClose();
                            }}
                        >
                            <Copy16Regular aria-hidden /> Duplizieren…
                        </button>
                    )}
                    {/* K2: Verknüpfen wieder aus dem Kalender heraus (E10 — kein
                        WRITE-Gate; auch auf CBB-/Lesekalendern verfügbar).
                        Nicht für die Ebene „Termine meiner Kontakte": ohne
                        Kalender-Leserecht lehnt das Backend das Verknüpfen ab. */}
                    {!ev.ueberKontakt && (
                    <>
                    <button
                        type="button"
                        role="menuitem"
                        className="cal-ctx-item"
                        onClick={() => {
                            // K10: der Kunde des Termins steuert im Picker die Rangstufe 1.
                            setZuweisenKontext({
                                mailbox: ev.mailbox,
                                eventId: ev.id,
                                contactId: ev.meta?.kundeId ?? null,
                            });
                            onClose();
                        }}
                    >
                        <LinkSquare16Regular aria-hidden />{" "}
                        {ev.meta?.vorgangId != null ? "Vorgang ändern…" : "Vorgang verknüpfen…"}
                    </button>
                    <button
                        type="button"
                        role="menuitem"
                        className="cal-ctx-item"
                        onClick={() => {
                            setNeuerVorgangKontext({
                                mailbox: ev.mailbox,
                                eventId: ev.id,
                                attendees: ev.attendees ?? [],
                                organizer: ev.organizer ?? null,
                            });
                            onClose();
                        }}
                    >
                        <Add16Regular aria-hidden /> Neuer Vorgang aus Termin…
                    </button>
                    </>
                    )}
                    {teamsUrl && (
                        <button
                            type="button"
                            role="menuitem"
                            className="cal-ctx-item"
                            onClick={() => {
                                window.open(teamsUrl, "_blank", "noopener,noreferrer");
                                onClose();
                            }}
                        >
                            <Video16Regular aria-hidden /> Teams-Meeting teilnehmen
                        </button>
                    )}
                </>
            ) : (
                <div className="cal-ctx-form">
                    <div className="cal-ctx-form-titel">Verschieben nach</div>
                    <input
                        type="date"
                        className="cal-ctx-input"
                        value={datum}
                        onChange={(e) => setDatum(e.target.value)}
                        aria-label="Neues Datum"
                    />
                    {!ev.allDay && (
                        <input
                            type="time"
                            step={900}
                            className="cal-ctx-input"
                            value={zeit}
                            onChange={(e) => setZeit(e.target.value)}
                            aria-label="Neue Startzeit"
                        />
                    )}
                    {/* Vorschau: Dauer bleibt konstant. */}
                    <div className="cal-ctx-preview">
                        {neu ? zeitraumLabel(neu.start, neu.end, ev.allDay) : "Ungültiges Datum"}
                    </div>
                    <div className="cal-ctx-actions">
                        <button type="button" className="cal-peek-btn" onClick={onClose}>
                            Abbrechen
                        </button>
                        <button
                            type="button"
                            className="cal-peek-btn cal-peek-btn-primary"
                            disabled={saving || !neu}
                            onClick={() => void submit()}
                        >
                            {saving ? "Verschiebe…" : "Verschieben"}
                        </button>
                    </div>
                </div>
            )}
            {dialoge}
        </div>,
        document.body,
    );
};

export default TerminContextMenu;
